import React, { useCallback, useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Table, Tooltip, Typography } from 'antd';

import classNames from 'classnames';
import Truncate from 'v2/components/Shared/TruncateText';
import { useListConnectProducts } from 'v2/hooks/useListConnectProducts';

import { useActionState } from '../../contexts/ActionStateContext';

const EnhancedTable: React.FC = () => {
  const { newAction, setNewAction } = useActionState();
  const [filter, setFilter] = useState('');

  const { products } = useListConnectProducts();

  const handleSelectAllClick = useCallback(
    (checked: boolean) => {
      const updatedAction = { ...newAction };

      if (checked) {
        updatedAction.products_id = products.map((n: any) => n.id);
        setNewAction(updatedAction);
        return;
      }

      updatedAction.products_id = [];
      setNewAction(updatedAction);
    },
    [setNewAction, newAction, products]
  );

  const handleClick = useCallback(
    (id: any) => {
      const updatedAction = { ...newAction };

      if (Array.isArray(updatedAction.products_id)) {
        const index = updatedAction.products_id.indexOf(id);

        if (index === -1) {
          updatedAction.products_id = [...updatedAction.products_id, id];
        } else {
          updatedAction.products_id = updatedAction.products_id.filter((_: any, i: number) => i !== index);
        }
      } else {
        updatedAction.products_id = [id];
      }

      setNewAction(updatedAction);
    },
    [setNewAction, newAction]
  );

  const isSelected = useCallback(
    (id: any) => Array.isArray(newAction.products_id) && newAction.products_id.indexOf(id) !== -1,
    [newAction.products_id]
  );

  const selectedProducts = newAction.products_id || [];
  const listProducts = Array.isArray(products) ? products : [];
  const filteredProducts = listProducts.filter((p: any) => {
    if (!filter || filter === '') {
      return true;
    }

    if (p?.title.toLowerCase().includes(filter.toLowerCase())) {
      return true;
    }

    if (p.id == filter) {
      return true;
    }

    return false;
  });

  const allSelected = listProducts.length > 0 && selectedProducts.length === listProducts.length;
  const indeterminate = selectedProducts.length > 0 && selectedProducts.length < listProducts.length;

  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={indeterminate}
          checked={allSelected}
          onChange={e => handleSelectAllClick(e.target.checked)}
        />
      ),
      key: 'checkbox',
      width: 50,
      render: (_: any, record: any) => <Checkbox checked={isSelected(record.id)} />
    },
    {
      title: (
        <Tooltip title='Sort' placement='bottomLeft'>
          <span>Produto</span>
        </Tooltip>
      ),
      key: 'name',
      render: (_: any, record: any) => (
        <>
          <strong>{record.id}</strong>
          <Truncate count={50} final='...'>
            {record.title}
          </Truncate>
        </>
      )
    }
  ];

  const toolbarHighlight = selectedProducts.length > 0;

  return (
    <div className='w-full rounded-sm bg-white shadow-sm'>
      <div className='p-4'>
        <label className='mb-1 block text-sm'>Buscar</label>
        <Input value={filter} onChange={e => setFilter(e.target.value)} />
      </div>
      <div
        className={classNames('flex w-full items-center px-4 py-2', toolbarHighlight && 'text-primary bg-primary-bg')}
      >
        <div className='flex-none'>
          {selectedProducts.length > 0 ? (
            <Typography.Text>
              {selectedProducts.length === 1
                ? `${selectedProducts.length} produto selecionado`
                : `${selectedProducts.length} produtos selecionados`}
            </Typography.Text>
          ) : (
            <Typography.Text>Selecione os produtos</Typography.Text>
          )}
        </div>
        <div className='flex-[1_1_100%]' />
        <div className='text-grey-400'>
          {selectedProducts.length > 0 ? (
            <Tooltip title='Deselecionar'>
              <Button type='text' icon={<CloseOutlined />} onClick={() => handleSelectAllClick(false)} />
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className='h-[40vh] w-full overflow-y-auto'>
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey='id'
          pagination={false}
          size='small'
          onRow={record => ({
            onClick: () => handleClick(record.id),
            style: { cursor: 'pointer' }
          })}
        />
      </div>
    </div>
  );
};

export default EnhancedTable;
